// A horizontal row of media, newest first, that opens on the canvas.
//
// Two rows on the resolve page are built from this:
//
//   DELIVERED      what answered the issue - sheets from a delivered set,
//                  a DWG / Excel / photo / video handed over, the
//                  screenshots of a submitted fix
//   MEDIA HISTORY  every source medium the issue has collected, in order:
//                  the picture it was raised with, each paste, markup
//                  save, attached file, camera recording, imported or
//                  picked sheet
//
// One click puts the thing on the canvas (the page decides which
// surface); a marked-up image saved from there becomes the newest entry
// and the issue's picture. Thumbnails: the image itself, a video frame,
// the first page of a PDF, a kind icon for the rest.
import React, { useEffect, useState } from "react";
import moment from "moment";
import { Button, Popconfirm } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import VersionChip from "components/Documents/VersionChip";
import { url } from "api";
import { T } from "pages/Issue/theme";
import { KIND_META } from "components/Issue/IssueSourceControls";
import { renderPdfThumbnail } from "helpers/pdfPreview";

const abs = (u) => (/^(https?:|data:|blob:)/i.test(u || "") ? u : `${url}${u}`);

function Thumb({ slide }) {
  const kind = slide.kind || "file";
  const meta = KIND_META[kind] || KIND_META.file;
  const Icon = meta.icon;
  const [pdfThumb, setPdfThumb] = useState(null);
  const wantsPdf = kind === "pdf" || (kind === "sheet" && /\.pdf(\.gz)?(\?|$)/i.test(slide.url || ""));
  useEffect(() => {
    let alive = true;
    setPdfThumb(null);
    if (wantsPdf && slide.url) renderPdfThumbnail(slide.url).then((s) => { if (alive && s) setPdfThumb(s); }).catch(() => {});
    return () => { alive = false; };
  }, [wantsPdf, slide.url]);
  const box = { width: 64, height: 44, borderRadius: 4, overflow: "hidden", background: "#f3f4f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
  if (kind === "image" || (kind === "sheet" && !wantsPdf)) return <div style={box}><img src={abs(slide.url)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} /></div>;
  if (kind === "video") return <div style={box}><video src={abs(slide.url)} muted preload="metadata" style={{ maxWidth: "100%", maxHeight: "100%" }} /></div>;
  if (pdfThumb) return <div style={box}><img src={pdfThumb} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /></div>;
  return <div style={box}><Icon style={{ fontSize: 22, color: meta.color }} /></div>;
}

// `onDelete(slide)` adds the same trash icon the attachments row has to
// every slide that carries `deletable` - the page decides who may (the
// project's owner / members) and which route each kind goes through.
export default function MediaHistoryStrip({ title, slides, activeUrl, onSelect, onDelete, accent, right, onBack, backLabel = "Back to current", empty }) {
  if (!slides.length && !right && !empty) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 24px", background: "#fff", borderBottom: `1px solid ${T.line}`, overflowX: "auto" }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: accent || T.muted, letterSpacing: ".06em", whiteSpace: "nowrap" }}>
        {title} ({slides.length})
      </div>
      {!slides.length && empty ? <span style={{ fontSize: 11.5, color: T.muted }}>{empty}</span> : null}
      {slides.map((v) => {
        const meta = KIND_META[v.kind] || KIND_META.file;
        const isActive = !!activeUrl && v.url === activeUrl;
        const when = v.createdAt ? moment(v.createdAt) : null;
        const tip = [
          `${v.label || meta.label}${v.name ? ` - ${v.name}` : ""}`,
          v.who ? `by ${v.who}` : "",
          when ? `on ${when.format("YYYY-MM-DD HH:mm")}` : "",
          v.isCurrent ? "(current image)" : "click to open on the canvas",
        ].filter(Boolean).join(" ");
        return (
          <div key={v.key || `${v.kind}-${v.url}`} onClick={() => onSelect && onSelect(v)} title={tip}
               style={{
                 display: "flex", alignItems: "center", gap: 8, padding: "4px 8px 4px 4px", borderRadius: 8, cursor: "pointer", flexShrink: 0,
                 border: `2px solid ${isActive ? T.navy : (v.tone === "added" ? T.added2 : T.line)}`,
                 background: isActive ? "#f5f9ff" : (v.tone === "added" ? T.addedBg : "#fff"),
               }}>
            <Thumb slide={v} />
            <div style={{ fontSize: 11.5, lineHeight: 1.25, maxWidth: 150 }}>
              <div style={{ fontWeight: 700, color: v.tone === "added" ? T.added2 : T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {v.label || meta.label}{v.isCurrent ? <span style={{ color: T.muted, fontWeight: 400 }}> (current)</span> : null}
                {v.versionDocId ? <VersionChip docId={v.versionDocId} rev={v.versionRev} style={{ marginLeft: 4 }} /> : null}
              </div>
              <div style={{ color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.name || v.who || meta.label}</div>
              <div style={{ color: T.muted }} title={when ? when.format("YYYY-MM-DD HH:mm") : ""}>{when ? when.format("MMM D, HH:mm") : (v.who || "")}</div>
            </div>
            {onDelete && v.deletable && (
              <span onClick={(e) => e.stopPropagation()}>
                <Popconfirm title={`Remove ${v.label || meta.label}${v.name ? ` (${v.name})` : ""} from this issue?`} okText="Remove" okButtonProps={{ danger: true }}
                            onConfirm={() => onDelete(v)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Remove from the media history" />
                </Popconfirm>
              </span>
            )}
          </div>
        );
      })}
      {onBack && (
        <Button size="small" onClick={onBack} style={{ marginLeft: 4 }}>{backLabel}</Button>
      )}
      {right ? <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.muted, whiteSpace: "nowrap" }}>{right}</span> : null}
    </div>
  );
}
